/**
 * "同步资产生产进度"链路的 iTrace 上报。
 *
 * 链路：(Agent 终态消息含 manclaw / 用户点击 manclaw 链接) → window CustomEvent → 各面板订阅 → 同步本地数据。
 *
 * 监控目标（仅埋必要点位，避免噪声）：
 *   1. 前端是否真的收到了进度信号（频率、类型分布、来源分布）
 *   2. 收到信号后面板是否真的把进度同步进 UI（耗时、失败率）
 *
 * WPK 字段分配（category=111，与 WS 的 110 区隔）：
 *   c1  — 事件相关的资源类型列表（逗号拼接，如 "video,storyboard-document"）
 *   c2  — conversationId（若取得到）
 *   c3  — 面板类型（sync 事件用，如 "knowledge_storyboard_episode"）
 *   c4  — 错误码（sync 失败时）
 *   c5  — 触发来源 'agent-push' / 'user-click'
 *
 *   wl_avgv1 — 资源个数（signal 事件）
 *   wl_avgv2 — 同步耗时 ms（sync 事件）
 *
 *   bl4 — 错误详情（sync 失败时）
 *
 * 所有方法内部 try-catch，监控异常不影响业务。
 */
import { itrace } from './itraceService';

const RESOURCE_PROGRESS_CATEGORY = 111;

function safeReport(log: Record<string, unknown>): void {
  try {
    itrace.report(log);
  } catch (err) {
    console.warn('[resourceProgressMonitor] report failed:', err);
  }
}

/**
 * 前端检测到 manclaw 资源链接（Agent 推送 或 用户点击），即将派发进度同步事件。
 *
 * @param types          命中的资源 scheme 集合（已去重）
 * @param count          提取到的资源条数（含同一类型多个 node_key）
 * @param source         触发来源：'agent-push' / 'user-click'
 * @param conversationId 可选：当前会话 id，便于按会话分布查询
 */
export function reportResourceProgressSignal(
  types: string[],
  count: number,
  source: string,
  conversationId?: string,
): void {
  safeReport({
    category: RESOURCE_PROGRESS_CATEGORY,
    msg: 'resource_progress_signal',
    w_succ: 1,
    wl_avgv1: count,
    c1: types.join(',') || undefined,
    c2: conversationId || undefined,
    c5: source,
  });
}

/**
 * 面板收到事件后的同步结果上报（成功 + 耗时；或失败 + 错误详情）。
 *
 * @param panel        面板类型标识（如 "knowledge_storyboard_episode"）
 * @param success      同步是否成功
 * @param durationMs   同步耗时（包含网络）
 * @param source       触发来源：'agent-push' / 'user-click'
 * @param errorMessage 失败时的错误信息
 * @param errorCode    可选错误码
 */
export function reportResourceProgressSync(
  panel: string,
  success: boolean,
  durationMs: number,
  source: string,
  errorMessage?: string,
  errorCode?: string,
): void {
  safeReport({
    category: RESOURCE_PROGRESS_CATEGORY,
    msg: 'resource_progress_sync',
    w_succ: success ? 1 : 0,
    wl_avgv2: durationMs,
    c3: panel,
    c4: errorCode,
    c5: source,
    bl4: errorMessage,
  });
}
